import { Controller } from 'lib_ts/enums/routing/controllers';
import { SessionEventsRoutes } from 'lib_ts/enums/routing/routes/session-events';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import {
  ILoginEvent,
  IMSTargetEvent,
  ISessionEvent,
  ISessionEventDetails,
} from 'lib_ts/interfaces/i-session-event';
import { ISessionSummary } from 'lib_ts/interfaces/i-session-summary';
import { BaseRESTService } from 'services/_base-rest.service';

export const getShortenedSession = (id: string | undefined): string => {
  if (!id) {
    return 'empty-id';
  }

  const parts = id?.split('-');
  const index = id.substring(id.lastIndexOf('.'));
  const shortSession =
    parts.length === 6
      ? `${parts[0]}-${parts[1]}*${index}`
      : `${parts[0]}*${index}`;
  return shortSession;
};

export class SessionEventsService extends BaseRESTService {
  private static instance: SessionEventsService;

  private static getInstance(): SessionEventsService {
    if (!SessionEventsService.instance) {
      SessionEventsService.instance = new SessionEventsService();
    }

    return SessionEventsService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Sessions,
    });
  }

  /** should only return sessions which contain at least 1 fire event */
  static async summarizeUserSessions(config: {
    userID: string;
    exclude_no_fires: boolean;
    limit: number;
  }): Promise<ISessionSummary[]> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.SummarizeUser,
      params: {
        userID: config.userID,
        exclude_no_fires: config.exclude_no_fires,
        limit: config.limit,
      } as any,
    });
  }

  /** should only return sessions which contain at least 1 fire event */
  static async summarizeMachinesSessions(config: {
    machineIDs: string[];
    start_date: Date;
    end_date: Date;
    exclude_no_fires: boolean;
    limit: number;
  }): Promise<ISessionSummary[]> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.SummarizeMachines,
      params: {
        machineIDs: config.machineIDs.join(','),
        start_date: config.start_date.toISOString(),
        end_date: config.end_date.toISOString(),
        exclude_no_fires: config.exclude_no_fires,
        limit: config.limit,
      } as any,
    });
  }

  static async createOne(
    data: Partial<ISessionEventDetails>
  ): Promise<IServerResponse> {
    return await this.getInstance().post(
      {
        uri: SessionEventsRoutes.CreateOne,
      },
      data
    );
  }

  static async getManyFromSession(
    session: string
  ): Promise<ISessionEventDetails[]> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.GetManyFromSession,
      params: {
        session: session,
      } as any,
    });
  }

  /** returns the (single) login event for the session */
  static async getLogin(session: string): Promise<ILoginEvent> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.GetLogin,
      params: {
        session: session,
      } as any,
    });
  }

  static async updateOne(data: Partial<ISessionEvent>): Promise<ISessionEvent> {
    if (!data._id) {
      throw new Error('Cannot put without id');
    }

    return await this.getInstance().put(
      {
        uri: SessionEventsRoutes.UpdateOne,
        params: {
          event_id: data._id,
        } as any,
      },
      data
    );
  }

  static async exportShots(config: {
    machineID: string;
    session: string;
  }): Promise<IMSTargetEvent[] | undefined> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.ExportShots,
      params: {
        machineID: config.machineID,
        session: config.session,
      } as any,
    });
  }
}
